import { env } from '@zopauk/nextjs-env';
import { render } from 'datocms-structured-text-to-plain-text';
import type { LandingPageFaqAccordionFragment, FaqAccordionFragment } from '@/utils/dato/generated';

type WithTypename = { __typename: string };

export const safeJsonLdStringify = (data: object): string => JSON.stringify(data).replace(/</g, '\\u003c');

export const generateOrganizationSchema = () => ({
  description:
    'Zopa Business provides embedded finance and payment solutions — including auto finance, retail finance, and partnership APIs — to UK businesses.',
  parentOrganization: {
    url: env('NEXT_PUBLIC_WEB_GALLERIA_URL'),
    '@type': 'Organization',
    name: 'Zopa Bank',
  },
  logo: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/images/banks/zopa.svg`,
  url: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/business`,
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Zopa Business',
  areaServed: 'GB',
});

export const generateWebSiteSchema = () => ({
  url: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/business`,
  '@context': 'https://schema.org',
  name: 'Zopa for Business',
  '@type': 'WebSite',
});

export const B2B_SERVICE_SCHEMAS: Record<string, { description: string; name: string }> = {
  'business/resources': {
    description:
      'Guides, insights and case studies to help your business grow with Zopa payments and lending solutions.',
    name: 'Zopa for Business Resources',
  },
  'business/auto-finance': {
    description:
      'Dealer finance solutions including hire purchase and PCP, with Zopa managing underwriting and repayments.',
    name: 'Zopa Auto Finance',
  },
  'business/partnerships': {
    description: 'Embed Zopa payments and lending products directly into your platform via a single API.',
    name: 'Zopa Embedded Finance & Partnerships',
  },
  'business/retail-finance': {
    description: 'Offer interest-free or interest-bearing finance at checkout. Zopa manages the risk and underwriting.',
    name: 'Zopa Retail Finance',
  },
  'business/case-studies': {
    description: 'Real-world examples of how businesses grow with Zopa lending and payments solutions.',
    name: 'Zopa Business Case Studies',
  },
  'business/payments': {
    description: 'Accept online and in-person payments through a single API. No vendor sprawl.',
    name: 'Zopa Payments',
  },
};

export const generateServiceSchema = (service: { description: string; name: string; url: string }) => ({
  provider: {
    url: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/business`,
    '@type': 'Organization',
    name: 'Zopa Business',
  },
  '@context': 'https://schema.org',
  description: service.description,
  '@type': 'Service',
  name: service.name,
  url: service.url,
});

export const generateFaqSchema = (faqs: FaqAccordionFragment['faqs']) => {
  const questions = faqs
    .filter((faq) => faq.title && faq.body)
    .map((faq) => ({
      acceptedAnswer: {
        text: render(faq.body) || '',
        '@type': 'Answer',
      },
      '@type': 'Question',
      name: faq.title,
    }));

  if (questions.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    mainEntity: questions,
    '@type': 'FAQPage',
  };
};

export const extractFaqModules = (modules: WithTypename[]): FaqAccordionFragment[] =>
  modules.filter((m): m is FaqAccordionFragment => m.__typename === 'FaqAccordionRecord');

export const extractLandingPageFaqModules = (modules: WithTypename[]): LandingPageFaqAccordionFragment[] =>
  modules.filter((m): m is LandingPageFaqAccordionFragment => m.__typename === 'LandingPageFaqAccordionRecord');

export const generateLandingPageFaqSchema = (faqs: LandingPageFaqAccordionFragment['faqs']) => {
  const questions = faqs
    .filter((faq) => faq.question && faq.answer)
    .map((faq) => ({ text: render(faq.answer)?.trim(), question: faq.question }))
    // An answer with an empty document is truthy but renders to no text, which Google rejects
    .filter(({ text }) => text)
    .map(({ question, text }) => ({
      acceptedAnswer: {
        '@type': 'Answer',
        text,
      },
      '@type': 'Question',
      name: question,
    }));

  if (questions.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    mainEntity: questions,
    '@type': 'FAQPage',
  };
};

export type BreadcrumbItem = {
  // Absolute URL
  item: string;
  name: string;
};

export const buildBreadcrumbList = (items: BreadcrumbItem[], id?: string) => ({
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.item,
  })),
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  ...(id && { '@id': id }),
});

// Builds the trail from the URL segments, used for the business pages
export const generateBreadcrumbSchema = (pageSlug: string) => {
  const baseUrl = env('NEXT_PUBLIC_WEB_GALLERIA_URL') ?? '';
  const segments = pageSlug.split('/').filter(Boolean);

  return buildBreadcrumbList([
    { item: baseUrl, name: 'Home' },
    ...segments.map((segment, i) => ({
      name: segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      item: `${baseUrl}/${segments.slice(0, i + 1).join('/')}`,
    })),
  ]);
};
